/**
 * Aba Faturamentos pendentes: competência (mês) + cliente opcional; tabela com
 * consumo por contrato e ação 'Faturar' abrindo o dialog de finalização.
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CircleCheck } from 'lucide-react';
import { MODALITY_LABELS } from '@erp/shared';
import type { PendingBilling } from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { Badge, EmptyState, Skeleton } from '@/components/ui/misc';
import type { ClientRef } from '@/features/tickets/ClientPicker';
import { errorMessage, pendingBillingsOptions } from './billing-queries';
import { currentCompetence, formatBRL, minutesToHHMM } from './billing-format';
import { ClientFilter } from './client-filter';
import { InvoiceCreateDialog } from './invoice-create-dialog';

export function PendingTab() {
  const [competence, setCompetence] = useState(currentCompetence());
  const [client, setClient] = useState<ClientRef | null>(null);
  const [toBill, setToBill] = useState<PendingBilling | null>(null);
  const [billed, setBilled] = useState<string | null>(null);

  const pendingQ = useQuery(
    pendingBillingsOptions({
      competence,
      client_ids: client ? [client.id] : undefined,
    })
  );

  useEffect(() => {
    if (billed === null) return;
    const t = setTimeout(() => setBilled(null), 4000);
    return () => clearTimeout(t);
  }, [billed]);

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-end gap-4 rounded-lg border bg-card p-4 shadow-xs">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="pending-competence" className="text-[13px]">
            Competência
          </Label>
          <Input
            id="pending-competence"
            type="month"
            className="h-8 w-44"
            value={competence}
            onChange={(e) => {
              if (e.target.value) setCompetence(e.target.value);
            }}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-[13px]">Cliente</Label>
          <ClientFilter selected={client} onSelect={setClient} />
        </div>
        {pendingQ.data ? (
          <span className="ml-auto pb-1.5 text-[13px] text-muted-foreground tabular-nums">
            {pendingQ.data.length}{' '}
            {pendingQ.data.length === 1 ? 'faturamento pendente' : 'faturamentos pendentes'}
          </span>
        ) : null}
      </div>

      {billed ? (
        <p
          role="status"
          className="flex items-center gap-1.5 rounded-lg border border-[#B7EB8F] bg-[#F6FFED] px-4 py-2.5 text-sm text-[#52C41A]"
        >
          <CircleCheck className="size-4" />
          Fatura gerada com sucesso para {billed}.
        </p>
      ) : null}

      {/* Tabela */}
      <div className="overflow-hidden rounded-lg border bg-card shadow-xs">
        {pendingQ.isPending ? (
          <div aria-busy className="space-y-3 p-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4">
                <Skeleton className="h-4 w-1/4" />
                <Skeleton className="h-4 w-1/4" />
                <Skeleton className="h-5 w-20 rounded-sm" />
                <Skeleton className="h-4 w-1/5" />
                <Skeleton className="h-4 w-24" />
              </div>
            ))}
          </div>
        ) : pendingQ.isError ? (
          <EmptyState
            message={`Erro ao carregar faturamentos pendentes: ${errorMessage(pendingQ.error)}`}
          />
        ) : pendingQ.data.length === 0 ? (
          <EmptyState message="Nenhum faturamento pendente na competência" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted text-left text-[13px]">
                  <th className="h-9 px-4 font-semibold">Cliente</th>
                  <th className="h-9 px-4 font-semibold">Contrato</th>
                  <th className="h-9 px-4 font-semibold">Modalidade</th>
                  <th className="h-9 px-4 font-semibold">Consumo</th>
                  <th className="h-9 px-4 font-semibold">Valor</th>
                  <th className="h-9 px-4 text-right font-semibold">
                    <span className="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {pendingQ.data.map((p) => {
                  const { consumption } = p;
                  const hasHours =
                    p.modality === 'hours' &&
                    consumption.workedMinutes != null &&
                    consumption.includedMinutes != null;
                  return (
                    <tr
                      key={p.contractId}
                      className="border-b border-divider transition-colors last:border-0 hover:bg-accent/60"
                    >
                      <td className="max-w-48 truncate px-4 py-2" title={p.clientName}>
                        {p.clientName}
                      </td>
                      <td className="max-w-56 truncate px-4 py-2 font-medium" title={p.contractName}>
                        {p.contractName}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        <Badge variant="primary">{MODALITY_LABELS[p.modality]}</Badge>
                      </td>
                      <td className="max-w-72 px-4 py-2">
                        {hasHours ? (
                          <>
                            <div className="tabular-nums">
                              <span className="font-semibold">
                                {minutesToHHMM(consumption.workedMinutes ?? 0)}
                              </span>
                              <span className="text-muted-foreground">
                                {' '}
                                / {minutesToHHMM(consumption.includedMinutes ?? 0)}
                              </span>
                            </div>
                            <div
                              className="truncate text-xs text-muted-foreground"
                              title={consumption.summary}
                            >
                              {consumption.summary}
                            </div>
                          </>
                        ) : (
                          <span
                            className="block truncate text-muted-foreground"
                            title={consumption.summary}
                          >
                            {consumption.summary}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 font-semibold whitespace-nowrap tabular-nums">
                        {formatBRL(p.baseValue)}
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex justify-end">
                          <Button size="sm" onClick={() => setToBill(p)}>
                            Faturar
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {toBill ? (
        <InvoiceCreateDialog
          pending={toBill}
          onCreated={() => {
            setBilled(toBill.contractName);
            setToBill(null);
          }}
          onClose={() => setToBill(null)}
        />
      ) : null}
    </div>
  );
}
